/* eslint-disable curly */
import * as React from 'react';
import classnames from 'classnames';
import { MediaLocation } from '@msdyn365-commerce/retail-proxy';

const ZoomedImage: React.FC<{
    baseClassName: string;
    closeZoomedInImage: () => void;
    zoomedImage: MediaLocation;
    goToPreviousButton?: React.ReactNode;
    goToNextButton?: React.ReactNode;
}> = ({
    baseClassName,
    closeZoomedInImage,
    zoomedImage,
    goToPreviousButton,
    goToNextButton
}) => {
    const DRAG_THRESHOLD = 5;

    const viewerRef = React.useRef<HTMLDivElement>(null);
    const imageRef = React.useRef<HTMLImageElement>(null);
    const observerRef = React.useRef<ResizeObserver | null>(null);

    const baseScaleRef = React.useRef(1);
    const stateRef = React.useRef({ x: 0, y: 0, scale: 1, zoomedIn: false });
    let { current: { startTx, startTy } } = React.useRef({ startTx: 0, startTy: 0 });
    let { current: { startX, startY } } = React.useRef({ startX: 0, startY: 0 });
    const [dragging, setDragging] = React.useState(false);
    const [cursor, setCursorState] = React.useState<'zoom-in' | 'zoom-out' | 'default'>('default');

    const computeBaseScale = () => {
        const vw = viewerRef.current?.clientWidth || 0;
        const vh = viewerRef.current?.clientHeight || 0;
        const nw = imageRef.current?.naturalWidth || 0;
        const nh = imageRef.current?.naturalHeight || 0;

        if (!vw || !vh || !nw || !nh) return 1;

        // fit inside viewer, never upscale above 1
        return Math.min(vw / nw, vh / nh, 1);
    };

    const getSizes = () => {
        return {
            vw: viewerRef.current?.clientWidth || 0,
            vh: viewerRef.current?.clientHeight || 0,
            iw: (imageRef.current?.naturalWidth || 0) * stateRef.current.scale,
            ih: (imageRef.current?.naturalHeight || 0) * stateRef.current.scale
        };
    };

    const setCursor = () => {
        const viewer = viewerRef.current;
        if (!viewer) return;

        const base = baseScaleRef.current;
        const s = stateRef.current.scale;

        const canZoomIn = base < 1 && Math.abs(s - base) < 1e-6;
        const canZoomOut = s > base + 1e-6;

        setCursorState(canZoomIn ? 'zoom-in' : canZoomOut ? 'zoom-out' : 'default');
    };

    const canPanNow = () => {
        const { vw, vh, iw, ih } = getSizes();
        return iw > vw || ih > vh;
    };

    const clampPan = () => {
        const { vw, vh, iw, ih } = getSizes();
        const cur = stateRef.current;

        // X
        if (iw <= vw) {
            cur.x = (vw - iw) / 2; // lock to center
        } else {
            const minX = vw - iw;
            cur.x = Math.max(minX, Math.min(0, cur.x));
        }

        // Y
        if (ih <= vh) {
            cur.y = (vh - ih) / 2; // lock to center
        } else {
            const minY = vh - ih;
            cur.y = Math.max(minY, Math.min(0, cur.y));
        }
    };

    const apply = () => {
        clampPan();
        const { x, y, scale } = stateRef.current;
        imageRef.current?.style.setProperty('transform', `translate(${x}px, ${y}px) scale(${scale})`);
    };

    const zoomTo = (newScale: number, clientX: number, clientY: number) => {
        const viewer = viewerRef.current!;
        const rect = viewer.getBoundingClientRect();
        const cx = clientX - rect.left;
        const cy = clientY - rect.top;

        const prevScale = stateRef.current.scale;
        const ix = (cx - stateRef.current.x) / prevScale;
        const iy = (cy - stateRef.current.y) / prevScale;

        stateRef.current.scale = newScale;
        stateRef.current.x = cx - ix * newScale;
        stateRef.current.y = cy - iy * newScale;

        apply();
        setCursor();
    };


    React.useEffect(() => {
        let dragging = false;
        let pointerDown = false;
        let moved = false;

        const onPointerDown = (e: PointerEvent) => {
            if (e.button !== 0 || (e.target as HTMLElement).tagName === 'BUTTON' || !canPanNow()) {
                return;
            }

            pointerDown = true;
            moved = false;

            // record start
            startX = e.clientX;
            startY = e.clientY;
            startTx = stateRef.current.x;
            startTy = stateRef.current.y;

            setDragging(dragging = true);
            viewer.setPointerCapture(e.pointerId);
        };
        const onPointerMove = (e: PointerEvent) => {
            if (!pointerDown || !dragging) {
                return;
            }

            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (!moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
                moved = true;
            }

            stateRef.current.x = startTx + dx;
            stateRef.current.y = startTy + dy;
            apply();
        };
        const onPointerUp = (e: PointerEvent) => {
            if (e.button !== 0 || (e.target as HTMLElement).tagName === 'BUTTON') return;

            const wasDragging = dragging;
            endDrag(e);
            pointerDown = false;

            // If we weren't dragging (or never really moved), treat as click toggle zoom
            if (!wasDragging || !moved) {
                const base = baseScaleRef.current;
                const s = stateRef.current.scale;
                if (base < 1 && Math.abs(s - base) < 1e-6) {
                    stateRef.current.zoomedIn = true;
                    zoomTo(1, e.clientX, e.clientY);
                } else if (s > base + 1e-6) {
                    stateRef.current.zoomedIn = false;
                    stateRef.current.scale = base;
                    stateRef.current.x = 0;
                    stateRef.current.y = 0;
                    apply();
                    setCursor();
                }
            }
        };
        const endDrag = (e?: PointerEvent) => {
            setDragging(dragging = false);
            if (e && viewer.hasPointerCapture(e.pointerId)) {
                viewer.releasePointerCapture(e.pointerId);
            }
        };
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();

            const viewer = viewerRef.current!;
            const rect = viewer.getBoundingClientRect();
            const cx = e.clientX - rect.left;
            const cy = e.clientY - rect.top;

            const prevScale = stateRef.current.scale;
            const zoom = Math.exp(-e.deltaY * 0.0015);

            const minScale = baseScaleRef.current;   // ✅ fit-to-view is minimum now
            const maxScale = 6;

            const nextScale = Math.max(minScale, Math.min(maxScale, prevScale * zoom));

            // Detect zoomedIn state based on change
            if (!stateRef.current.zoomedIn && nextScale > minScale + 1e-6) {
                stateRef.current.zoomedIn = true;
            } else if (stateRef.current.zoomedIn && Math.abs(nextScale - minScale) < 1e-6) {
                stateRef.current.zoomedIn = false;
            }

            if (Math.abs(nextScale - prevScale) < 1e-6) return;

            // anchor zoom
            const ix = (cx - stateRef.current.x) / prevScale;
            const iy = (cy - stateRef.current.y) / prevScale;

            stateRef.current.scale = nextScale;
            stateRef.current.x = cx - ix * nextScale;
            stateRef.current.y = cy - iy * nextScale;

            apply();
            setCursor();
        };

        const viewer = viewerRef.current!;
        viewer.style.setProperty('visibility', 'hidden');
        viewer.addEventListener('pointerdown', onPointerDown);
        viewer.addEventListener('pointermove', onPointerMove);
        viewer.addEventListener('pointerup', onPointerUp);
        viewer.addEventListener('pointercancel', endDrag);
        viewer.addEventListener('wheel', onWheel, { passive: false });

        let prevSize = { w: 0, h: 0 };

        observerRef.current = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            const entry = entries[0];

            if (!entry) return;

            const newW = entry.contentRect.width;
            const newH = entry.contentRect.height;

            baseScaleRef.current = computeBaseScale();

            // if user is at "default fit" mode, keep it fitting on resize
            if (stateRef.current.zoomedIn === false) {
                stateRef.current.scale = baseScaleRef.current;
                stateRef.current.x = 0;
                stateRef.current.y = 0;
            } else {
                // Adjust pan to keep focus point in place
                const diffX = (newW - prevSize.w) / 2;
                const diffY = (newH - prevSize.h) / 2;
                stateRef.current.x += diffX;
                stateRef.current.y += diffY;
            }

            prevSize = { w: newW, h: newH };

            apply();
            setCursor();
        });
        observerRef.current.observe(viewer);

        return () => {
            viewer?.removeEventListener('pointerdown', onPointerDown);
            viewer?.removeEventListener('pointermove', onPointerMove);
            viewer?.removeEventListener('pointerup', onPointerUp);
            viewer?.removeEventListener('pointercancel', endDrag);
            viewer?.removeEventListener('wheel', onWheel);
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, []);

    return (
        <div
            ref={viewerRef}
            className={`${baseClassName}__zoomed-in-image__image-container`}
            /* onClick={closeZoomedInImage} */
        >
            <img
                ref={imageRef}
                className={classnames(`${baseClassName}__zoomed-in-image__image`, { dragging })}
                src={zoomedImage.Uri}
                title={zoomedImage.AltText}
                style={{ cursor }}
                onLoad={() => {
                    requestAnimationFrame(() => {
                        baseScaleRef.current = computeBaseScale();
                        stateRef.current.zoomedIn = false;
                        stateRef.current.scale = baseScaleRef.current;
                        stateRef.current.x = 0;
                        stateRef.current.y = 0;
                        apply();
                        setCursor();
                        if (viewerRef.current) {
                            viewerRef.current.style.setProperty('visibility', 'visible');
                        }
                    });
                }}
            />
            {goToPreviousButton}
            {goToNextButton}
        </div>
    );
};

export default ZoomedImage;
